import { usePrevious } from "@uidotdev/usehooks";
import { useCallback, useEffect, useRef, useState } from "react";

type UseTrackChangesOptions<T> = {
  /**
   * Function to extract a unique identifier from each item
   * @param item - The item to extract the ID from
   * @returns A string or number that uniquely identifies the item
   */
  getId: (item: T) => string | number;

  /**
   * How long to keep items marked as "new" (in milliseconds)
   * @default 5000
   */
  highlightDuration?: number;

  /**
   * Whether to track items that were removed from the list
   * @default false
   */
  trackRemovals?: boolean;
};

type UseTrackChangesResult<T> = {
  /**
   * Set of IDs that are newly added since the last change
   */
  newItemIds: Set<string | number>;

  /**
   * Set of IDs that were removed since the last change (if trackRemovals is true)
   */
  removedItemIds: Set<string | number>;

  /**
   * Function to check if an item is newly added
   */
  isNew: (item: T) => boolean;

  /**
   * Function to check if an item was removed (if trackRemovals is true)
   */
  isRemoved: (id: string | number) => boolean;

  /**
   * Function to manually clear all tracking state
   */
  clearTracking: () => void;
};

/**
 * Hook to track changes in an array of items and identify newly added or removed items
 *
 * @param items - Array of items to track
 * @param options - Configuration options
 * @returns Object with tracking state and helper functions
 *
 * @example
 * ```tsx
 * const { isNew, newItemIds } = useTrackChanges(alerts, {
 *   getId: (alert) => alert.id,
 *   highlightDuration: 3000
 * });
 *
 * return (
 *   <div>
 *     {alerts.map(alert => (
 *       <div key={alert.id} className={isNew(alert) ? 'highlight' : ''}>
 *         {alert.name}
 *       </div>
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useTrackChanges<T>(
  items: T[],
  options: UseTrackChangesOptions<T>
): UseTrackChangesResult<T> {
  const { getId, highlightDuration = 5000, trackRemovals = false } = options;

  const previousItems = usePrevious(items);
  const [newItemIds, setNewItemIds] = useState<Set<string | number>>(new Set());
  const [removedItemIds, setRemovedItemIds] = useState<Set<string | number>>(
    new Set()
  );

  // Use ref to store the latest getId function to avoid dependency issues
  const getIdRef = useRef(getId);
  getIdRef.current = getId;

  useEffect(() => {
    if (previousItems && previousItems.length > 0) {
      const currentGetId = getIdRef.current;
      const previousIds = new Set(previousItems.map(currentGetId));
      const currentIds = new Set(items.map(currentGetId));

      // Find newly added items
      const newIds = new Set(
        [...currentIds].filter((id) => !previousIds.has(id))
      );

      // Find removed items (if tracking removals)
      const removedIds = trackRemovals
        ? new Set([...previousIds].filter((id) => !currentIds.has(id)))
        : new Set<string | number>();

      // Update state for new items
      if (newIds.size > 0) {
        setNewItemIds(newIds);

        // Clear highlighting after specified duration
        const newItemsTimer = setTimeout(() => {
          setNewItemIds(new Set());
        }, highlightDuration);

        return () => clearTimeout(newItemsTimer);
      }

      // Update state for removed items
      if (trackRemovals && removedIds.size > 0) {
        setRemovedItemIds(removedIds);

        // Clear removed tracking after specified duration
        const removedItemsTimer = setTimeout(() => {
          setRemovedItemIds(new Set());
        }, highlightDuration);

        return () => clearTimeout(removedItemsTimer);
      }
    }
  }, [items, previousItems, highlightDuration, trackRemovals]);

  const isNew = useCallback(
    (item: T): boolean => {
      return newItemIds.has(getIdRef.current(item));
    },
    [newItemIds]
  );

  const isRemoved = useCallback(
    (id: string | number): boolean => {
      return removedItemIds.has(id);
    },
    [removedItemIds]
  );

  const clearTracking = useCallback((): void => {
    setNewItemIds(new Set());
    setRemovedItemIds(new Set());
  }, []);

  return {
    newItemIds,
    removedItemIds,
    isNew,
    isRemoved,
    clearTracking,
  };
}
